// Scripted stand-ins for the whole port set, shared by both glasses clients'
// "?fake=1" mode. Nothing here reads import.meta.env or window at module top
// level; callers pass in whatever environment values they need.
import {
  FakeAuth,
  FakeDiscovery,
  FakeExperience,
  fakeBindings,
} from './FakePorts';
import { flagEnabled } from './pageFlags';
import type { ExperienceSessionPorts } from './ExperienceSession';
import type {
  ActionResponse,
  ExperienceConnection,
  ExperienceNavigation,
  TalkActionIntent,
  TextTurnIntent,
  Utterance,
} from '../../ports/ExperiencePort';
import type { PresentationDirective } from './presentationDirectives';

/** The dev gateway on the Tailscale network, used when no build-time override is set. */
export const DEFAULT_GAIA_BASE_URL = 'https://aeonia-dev.taile9e9a7.ts.net';

/** Opening the page with "?fake=1" runs the whole loop against scripted ports. */
export function fakeModeEnabled(search: string): boolean {
  return flagEnabled(search, 'fake');
}

/** How long the scripted ports wait before each answer, in milliseconds. */
export const FAKE_DELAYS = { connected: 300, opening: 500, reply: 800 } as const;

/** The first thing the character says once the connection is up. */
export const FAKE_OPENING_LINE = 'Well. Hello there.';

/**
 * A fake socket that answers like a server: it connects, greets, and echoes
 * every talk back a beat later.
 */
export interface ScriptedExperienceOptions {
  /** "?fake=1&nav=1": delivers this target as a display.map directive on a line (build note #3/#5). */
  navigation?: ExperienceNavigation | null;
  /** "?fake=1&cues=1": delivers a line with a display.text cue and a display.choice (build note #5). */
  cues?: boolean;
}

export class ScriptedExperience extends FakeExperience {
  private readonly navigation: ExperienceNavigation | null;
  private readonly cues: boolean;

  constructor(options: ScriptedExperienceOptions = {}) {
    super();
    this.navigation = options.navigation ?? null;
    this.cues = options.cues ?? false;
  }

  connect(token: string, experienceId: string, handlers: ExperienceConnection): void {
    super.connect(token, experienceId, handlers);
    setTimeout(() => {
      this.emitConnected(fakeBindings());
      setTimeout(() => this.emitActionResponse(reply(FAKE_OPENING_LINE)), FAKE_DELAYS.opening);
      if (this.navigation) {
        setTimeout(() => this.emitUtterance(fakeMapUtterance(this.navigation as ExperienceNavigation)), FAKE_DELAYS.opening);
      }
      if (this.cues) {
        setTimeout(() => this.emitUtterance(fakeCuesUtterance()), FAKE_DELAYS.opening);
      }
    }, FAKE_DELAYS.connected);
  }

  sendTalk(action: TalkActionIntent): void {
    super.sendTalk(action);
    setTimeout(
      () => this.emitActionResponse(reply(`You said: ${action.message}`)),
      FAKE_DELAYS.reply,
    );
  }

  sendTextTurn(turn: TextTurnIntent): void {
    super.sendTextTurn(turn);
    setTimeout(
      () => this.emitActionResponse(reply(`You said: ${turn.text}`, 'text_turn', turn.requestId)),
      FAKE_DELAYS.reply,
    );
  }
}

function reply(message: string, action = 'talk', request_id?: string): ActionResponse {
  return { type: 'action_response', action, success: true, message, request_id };
}

/**
 * A fixed navigation target near downtown Berkeley (same fixture the map lab
 * uses), for "?fake=1&nav=1" demos with no server.
 */
export function fakeNavigation(overrides: Partial<ExperienceNavigation> = {}): ExperienceNavigation {
  return {
    type: 'experience.navigation',
    sessionId: 'session-1',
    stepId: 'walk',
    show: 'compass',
    target: {
      lat: 37.8679509,
      lng: -122.2688968,
      label: 'Central Library',
      arrivalRadiusM: 40,
    },
    ...overrides,
  };
}

/** Builds the display.map directive an "?fake=1&nav=1" utterance carries, from the fixed fake target. */
function fakeMapUtterance(navigation: ExperienceNavigation): Utterance {
  const target = navigation.target;
  const directive: PresentationDirective = {
    m: 'display.map',
    p: {
      target: target
        ? { lat: target.lat, lng: target.lng, label: target.label, arrival_radius_m: target.arrivalRadiusM }
        : { lat: 0, lng: 0 },
      view: navigation.show,
    },
  };
  return {
    type: 'experience.utterance',
    sessionId: navigation.sessionId,
    utteranceId: 'fake-nav-utterance',
    speaker: {},
    text: `Head to ${target?.label ?? 'the target'}.`,
    // A walking step waits for the player to arrive; arrival is its answer, so no mic or keyboard shows.
    source: { stepId: navigation.stepId, response: { awaiting: true, stepId: navigation.stepId, accepts: ['client_event'] } },
    presentationDirectives: [directive],
  };
}

/** The "?fake=1&cues=1" demo line: a display.text cue at offset 800ms, and a display.choice, on a step that accepts a typed/spoken answer. */
function fakeCuesUtterance(): Utterance {
  return {
    type: 'experience.utterance',
    sessionId: 'session-1',
    utteranceId: 'fake-cues-utterance',
    speaker: {},
    text: 'Before we go on: yes, or no?',
    source: { stepId: 'demo-cues', response: { awaiting: true, stepId: 'demo-cues', accepts: ['voice_transcript'] } },
    presentationDirectives: [
      { m: 'display.text', p: { text: 'Two streets north.' }, offsetMs: 800 },
      { m: 'display.choice', p: { options: ['Yes', 'No'] } },
    ],
  };
}

/** Builds the scripted auth, discovery, and experience ports for fake mode. */
export function createScriptedPorts(options: ScriptedExperienceOptions = {}): ExperienceSessionPorts {
  return {
    auth: new FakeAuth(),
    discovery: new FakeDiscovery(),
    experience: new ScriptedExperience(options),
  };
}
